import { useWorldReadyContext } from '@client/contexts/WorldReadyContext';
import { getLocalizedString } from '@client/utils/localeHelper';
import { recordImpression } from '@common/analytics';
import React, { useEffect, useMemo } from 'react';
import ErrorView, { ErrorViewProps } from './ErrorView';

export enum ErrorPageVariant {
  NotFound = 'not_found',
  DuplicateButtonFailed = 'duplicate_button_failed',
  BusinessAccountRequired = 'business_account_required',
  SecondaryUserPrivilege = 'secondary_user_privilege',
  Unknown = 'unknown',
  AccountRestricted = 'account_restricted'
}

interface ErrorPageProps {
  variant: ErrorPageVariant;
  isErrorBoundary?: boolean;
}

const ErrorPage = ({ variant, isErrorBoundary = false }: ErrorPageProps) => {
  const worldReady = useWorldReadyContext();

  useEffect(() => {
    recordImpression({
      eventName: 'ncps_error_page_shown',
      error_type: variant,
      is_error_boundary: isErrorBoundary
    });
  }, [variant, isErrorBoundary]);

  const props = useMemo<ErrorViewProps>(() => {
    switch (variant) {
      case ErrorPageVariant.NotFound:
        return {
          title: getLocalizedString(worldReady, 'error.text.somethingWentWrong.title'),
          subtitle: getLocalizedString(worldReady, 'error.text.pageNotFound'),
          action: {
            text: getLocalizedString(worldReady, 'error.text.returnToDashboard'),
            href: '/mep/dashboard'
          }
        };
      case ErrorPageVariant.DuplicateButtonFailed:
        return {
          title: getLocalizedString(worldReady, 'error.text.somethingWentWrong.title'),
          subtitle: getLocalizedString(worldReady, 'error.text.duplicateButton')
        };
      case ErrorPageVariant.BusinessAccountRequired:
        return {
          title: getLocalizedString(worldReady, 'error.text.accountCheckFailed'),
          subtitle: getLocalizedString(worldReady, 'error.text.businessAccountSetupGuide'),
          action: {
            text: getLocalizedString(worldReady, 'error.text.createBusinessAccount'),
            href: '/myaccount/bundle/business/upgrade'
          }
        };
      case ErrorPageVariant.SecondaryUserPrivilege:
        return {
          title: getLocalizedString(worldReady, 'error.text.somethingWentWrong.title'),
          subtitle: getLocalizedString(worldReady, 'error.text.unauthorizedUser'),
          action: {
            text: getLocalizedString(worldReady, 'error.text.returnToDashboard'),
            href: '/mep/dashboard'
          }
        };
      case ErrorPageVariant.AccountRestricted:
        return {
          title: getLocalizedString(worldReady, 'error.text.accountRestricted.title'),
          subtitle: getLocalizedString(worldReady, 'error.text.accountRestricted.description'),
          action: {
            text: getLocalizedString(worldReady, 'error.text.accountRestricted.returnToHome'),
            href: '/mep/dashboard'
          }
        };
      case ErrorPageVariant.Unknown:
      default:
        return {
          title: getLocalizedString(worldReady, 'error.text.somethingWentWrong.title'),
          subtitle: getLocalizedString(worldReady, 'error.text.loadingIssue'),
          action: {
            text: getLocalizedString(worldReady, 'error.text.refresh'),
            onClick: () => window.location.reload()
          }
        };
    }
  }, [variant, worldReady]);

  return <ErrorView {...props} />;
};

export default ErrorPage;
